/**
 * Centralized color mappings for consistent styling across the application
 * Palette: #000000, #FFFFFF, #D54927 (brand), #C37C42 at 50% (brand-muted)
 */

// Background color mappings for sections
export const sectionBackgroundColors: Record<string, string> = {
  "white": "bg-white",
  "black": "bg-black text-white",
  "brand": "bg-brand text-white",
};

/**
 * Get background colour classes for any block or component
 *
 * @param colour - Colour key (e.g., "brand", "white", "black") or null/undefined
 * @returns Tailwind CSS classes for the background colour
 */
export const getBackgroundColour = (
  colour?: string | null
): string => {
  if (!colour) return sectionBackgroundColors.white;
  return sectionBackgroundColors[colour] || sectionBackgroundColors.white;
};

// Button style mappings
export const buttonStyles: Record<string, string> = {
  "primary": "bg-brand text-white hover:bg-brand-dark transition-colors",
  "secondary": "bg-white text-black border-2 border-black hover:bg-gray-100 transition-colors",
  "outline": "bg-transparent text-current border-2 border-current hover:bg-white/10 transition-colors",
};

/**
 * @deprecated Use containerWidths from @/lib/constants instead
 */
export { containerWidths, getContainerWidth } from '@/lib/constants';

// Padding size mappings
export const paddingSizes: Record<string, string> = {
  "none": "",
  "small": "py-8",
  "medium": "py-16",
  "large": "py-24",
  "xlarge": "py-32",
};

// Card color schemes
export const cardColorSchemes = {
  white: {
    background: "bg-white",
    text: "text-black",
    tag: "bg-gray-100 text-black",
    whatsIncluded: "bg-white",
    plus: "bg-gray-50",
    button: "bg-brand text-white hover:bg-brand-dark",
  },
  black: {
    background: "bg-black",
    text: "text-white",
    tag: "bg-white/10 text-white",
    whatsIncluded: "bg-black/90",
    plus: "bg-gray-900",
    button: "bg-white text-black hover:bg-gray-100",
  },
  brand: {
    background: "bg-brand",
    text: "text-white",
    tag: "bg-white/20 text-white",
    whatsIncluded: "bg-brand/90",
    plus: "bg-brand-dark",
    button: "bg-white text-black hover:bg-gray-100",
  },
};

// Type for card color scheme
type CardColorScheme = typeof cardColorSchemes.brand;

/**
 * Get card color scheme
 *
 * @param scheme - Color scheme key (e.g., "white", "black", "brand") or null/undefined
 * @returns Color scheme object with background, text, tag, button etc. classes
 */
export const getCardColorScheme = (
  scheme?: string | null
): CardColorScheme => {
  if (!scheme) return cardColorSchemes.white;
  return cardColorSchemes[scheme as keyof typeof cardColorSchemes] || cardColorSchemes.white;
};

/**
 * Determines if a background is dark
 *
 * @param backgroundColor - The background color key
 * @param hasBackgroundImage - Whether the section has a background image
 * @returns true if background is dark (needs light text)
 */
export const isDarkBackground = (
  backgroundColor: string,
  hasBackgroundImage?: boolean
): boolean => {
  if (hasBackgroundImage) return true;
  return ["brand", "black"].includes(backgroundColor);
};

/**
 * Get adaptive card background classes based on section styling
 *
 * @param backgroundColor - The background color key
 * @param hasBackgroundImage - Whether the section has a background image
 * @returns Tailwind classes for card background
 */
export const getCardBackground = (
  backgroundColor: string,
  hasBackgroundImage?: boolean
): string => {
  if (hasBackgroundImage) return "bg-white";

  const isDark = isDarkBackground(backgroundColor);
  if (isDark) return "bg-white/10 backdrop-blur-sm";

  return "bg-black/5";
};

/**
 * Determines if text should be dark (for use on light card backgrounds)
 *
 * @param backgroundColor - Section background color
 * @param hasBackgroundImage - Whether section has background image
 * @returns true if text should be dark
 */
export const shouldUseDarkText = (
  backgroundColor: string,
  hasBackgroundImage?: boolean
): boolean => {
  if (hasBackgroundImage) return true;
  const isDark = isDarkBackground(backgroundColor);
  if (isDark) return false;
  return true;
};

/**
 * Get button styles based on background color
 */
export const getButtonStyles = (backgroundColor: string, variant: "primary" | "secondary" = "primary"): string => {
  const isDark = isDarkBackground(backgroundColor);

  if (variant === "primary") {
    return isDark
      ? "bg-white text-black hover:bg-gray-100"
      : "bg-brand text-white hover:bg-brand-dark";
  }

  // secondary variant
  return isDark
    ? "bg-transparent text-white border-2 border-white hover:bg-white hover:text-black"
    : "bg-transparent text-black border-2 border-black hover:bg-black hover:text-white";
};
